import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  HiOutlineGlobeAlt,
  HiOutlineShieldCheck,
  HiOutlineExclamationCircle,
  HiOutlineChartBar,
  HiOutlineMagnifyingGlass,
  HiOutlineXMark,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineChevronDown,
} from 'react-icons/hi2';
import { DAV_APIS } from '../../../../adapters';
import { AuditLogProps } from '../../../../constants';
import { Card } from '../../../ui/Card';

const CATEGORY_STYLES: Record<string, { bg: string; text: string; dot: string; label: string }> = {
  AUTH:     { bg: 'bg-blue-100 dark:bg-blue-900/30',    text: 'text-blue-700 dark:text-blue-400',    dot: 'bg-blue-500',    label: 'Auth' },
  STAFF:    { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', dot: 'bg-purple-500',  label: 'Staff' },
  TOURS:    { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500', label: 'Tours' },
  BOOKINGS: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', dot: 'bg-orange-500',  label: 'Bookings' },
  WEBSITE:  { bg: 'bg-gray-100 dark:bg-gray-700/50',    text: 'text-gray-600 dark:text-gray-400',    dot: 'bg-gray-400',    label: 'Website' },
  CONTENT:  { bg: 'bg-teal-100 dark:bg-teal-900/30',    text: 'text-teal-700 dark:text-teal-400',    dot: 'bg-teal-500',    label: 'Content' },
  AGENTS:   { bg: 'bg-amber-100 dark:bg-amber-900/30',  text: 'text-amber-700 dark:text-amber-400',  dot: 'bg-amber-500',   label: 'Agents' },
  SYSTEM:   { bg: 'bg-slate-100 dark:bg-slate-700/50',  text: 'text-slate-600 dark:text-slate-400',  dot: 'bg-slate-400',   label: 'System' },
};

const ROLE_STYLES: Record<string, { bg: string; text: string }> = {
  website:         { bg: 'bg-gray-100 dark:bg-gray-700',        text: 'text-gray-500 dark:text-gray-400' },
  super_admin:     { bg: 'bg-red-100 dark:bg-red-900/30',       text: 'text-red-600 dark:text-red-400' },
  admin:           { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-600 dark:text-indigo-400' },
  content_manager: { bg: 'bg-teal-100 dark:bg-teal-900/30',     text: 'text-teal-600 dark:text-teal-400' },
  tour_operator:   { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400' },
  agent:           { bg: 'bg-amber-100 dark:bg-amber-900/30',   text: 'text-amber-600 dark:text-amber-400' },
};

const ACTION_LABELS: Record<string, string> = {
  LOGIN: 'Login', REGISTER: 'Register', FORGOT_PASSWORD: 'Forgot Password',
  RESET_PASSWORD: 'Reset Password', CHANGE_PASSWORD: 'Change Password',
  VERIFY_EMAIL: 'Verify Email', RESEND_VERIFICATION: 'Resend Verification',
  VIEW_PROFILE: 'View Profile', UPDATE_PROFILE: 'Update Profile',
  VIEW_STAFF: 'View Staff', INVITE_STAFF: 'Invite Staff', APPROVE_STAFF: 'Approve Staff',
  DEACTIVATE_STAFF: 'Deactivate Staff', DELETE_STAFF: 'Delete Staff',
  UPDATE_ROLE: 'Update Role', EDIT_STAFF: 'Edit Staff', RESEND_INVITATION: 'Resend Invite',
  VIEW_AGENTS: 'View Agents', VIEW_ACTIVE_AGENTS: 'View Active Agents',
  VIEW_INACTIVE_AGENTS: 'View Inactive Agents',
  ACTIVATE_AGENT: 'Activate Agent', DEACTIVATE_AGENT: 'Deactivate Agent',
  DELETE_AGENT: 'Delete Agent', UPDATE_AGENT: 'Update Agent',
  VIEW_TOURS: 'View Tours', CREATE_TOUR: 'Create Tour', UPDATE_TOUR: 'Update Tour', DELETE_TOUR: 'Delete Tour',
  VIEW_BOOKINGS: 'View Bookings', BOOKING_SUBMITTED: 'Tour Booking',
  UPDATE_BOOKING: 'Update Booking', DELETE_BOOKING: 'Delete Booking',
  SITE_BOOKING: 'Site Booking', VIEW_SITE_BOOKINGS: 'View Site Bookings',
  VIEW_INVOICES: 'View Invoices', CREATE_INVOICE: 'Create Invoice',
  UPDATE_INVOICE: 'Update Invoice', DELETE_INVOICE: 'Delete Invoice',
  VIEW_INQUIRIES: 'View Inquiries', INQUIRY_SUBMITTED: 'Inquiry', DELETE_INQUIRY: 'Delete Inquiry',
  VIEW_SUBSCRIBERS: 'View Subscribers', NEWSLETTER_SUBSCRIBE: 'Subscribe',
  DELETE_SUBSCRIBER: 'Remove Subscriber',
  SUPPORT_TICKET: 'Support Ticket', VIEW_SUPPORT: 'View Support',
  VIEW_REVIEWS: 'View Reviews', SUBMIT_REVIEW: 'Submit Review', DELETE_REVIEW: 'Delete Review',
  CREATE_POST: 'Create Post', UPDATE_POST: 'Update Post', DELETE_POST: 'Delete Post',
  CREATE_COUNTRY: 'Create Country', UPDATE_COUNTRY: 'Update Country', DELETE_COUNTRY: 'Delete Country',
  CREATE_CATEGORY: 'Create Category', UPDATE_CATEGORY: 'Update Category', DELETE_CATEGORY: 'Delete Category',
  CREATE_PARK: 'Create Park', UPDATE_PARK: 'Update Park', DELETE_PARK: 'Delete Park',
  CREATE_ACCOMMODATION: 'Create Accommodation', UPDATE_ACCOMMODATION: 'Update Accommodation', DELETE_ACCOMMODATION: 'Delete Accommodation',
  CREATE_PLACE: 'Create Place', UPDATE_PLACE: 'Update Place', DELETE_PLACE: 'Delete Place',
  CREATE_LANDING_PAGE: 'Create Landing Page', UPDATE_LANDING_PAGE: 'Update Landing Page', DELETE_LANDING_PAGE: 'Delete Landing Page',
  CREATE_SEO: 'Create SEO', UPDATE_SEO: 'Update SEO',
  VIEW_STATS: 'View Stats', VIEW_REPORT: 'View Report',
  VIEW_AUDIT_LOG: 'View Audit Log', FILE_UPLOAD: 'File Upload',
  GET_REQUEST: 'GET Request', POST_REQUEST: 'POST Request',
  PATCH_REQUEST: 'PATCH Request', DELETE_REQUEST: 'DELETE Request', PUT_REQUEST: 'PUT Request',
};

const formatTimestamp = (dateStr: string) => {
  const d = new Date(dateStr);
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return { date, time };
};

const formatRole = (role: string): string =>
  role.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const formatRelative = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
};

interface FiltersState {
  search: string; category: string; role: string; status: string; dateFrom: string; dateTo: string;
}
interface StatsState {
  todayTotal: number; websiteToday: number; adminToday: number; failuresToday: number;
}
const EMPTY_FILTERS: FiltersState = { search: '', category: '', role: '', status: '', dateFrom: '', dateTo: '' };

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className='flex flex-col gap-0.5'>
    <span className='text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500'>{label}</span>
    <span className='text-xs font-medium text-gray-700 dark:text-gray-300 break-all'>{value || <span className='text-gray-400'>—</span>}</span>
  </div>
);

const ExpandedDetail = ({ log }: { log: AuditLogProps }) => {
  const { date, time } = formatTimestamp(log.createdAt);
  const methodColors: Record<string, string> = {
    GET: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
    POST: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
    PATCH: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20',
    PUT: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20',
    DELETE: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
  };
  const methodColor = methodColors[log.method] ?? 'text-gray-600 bg-gray-100';

  return (
    <div className='px-4 py-4 bg-gray-50/80 dark:bg-gray-800/40 border-t border-gray-100 dark:border-gray-800'>
      <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4'>
        <DetailRow label='Date' value={date} />
        <DetailRow label='Time' value={time} />
        <DetailRow label='HTTP Method' value={
          <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold ${methodColor}`}>{log.method}</span>
        } />
        <DetailRow label='Endpoint' value={<span className='font-mono text-[11px]'>{log.path}</span>} />
        <DetailRow label='Result' value={
          log.status === 'success' ? (
            <span className='flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-bold text-xs'>
              <HiOutlineCheckCircle className='w-3.5 h-3.5' /> Success
            </span>
          ) : (
            <span className='flex items-center gap-1 text-red-500 dark:text-red-400 font-bold text-xs'>
              <HiOutlineXCircle className='w-3.5 h-3.5' /> Failed
            </span>
          )
        } />
        <DetailRow label='IP Address' value={<span className='font-mono text-[11px]'>{log.actor.ip}</span>} />
        <DetailRow label='Actor Email' value={log.actor.email} />
        <DetailRow label='Username' value={log.actor.username} />
        <DetailRow label='Role' value={log.actor.role ? formatRole(log.actor.role) : null} />
        <DetailRow label='User ID' value={<span className='font-mono text-[10px]'>{log.actor.userId}</span>} />
        <DetailRow label='Resource Type' value={log.resource.type} />
        <DetailRow label='Resource ID' value={<span className='font-mono text-[10px]'>{log.resource.id}</span>} />
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, colorClass }: { icon: React.ReactNode; label: string; value: number; colorClass: string }) => (
  <Card variant='glass' className='p-5'>
    <div className='flex items-center gap-4'>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClass}`}>{icon}</div>
      <div>
        <p className='text-2xl font-extrabold text-gray-900 dark:text-white'>{value.toLocaleString()}</p>
        <p className='text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5'>{label}</p>
      </div>
    </div>
  </Card>
);

const AuditPage = () => {
  const [logs, setLogs] = useState<AuditLogProps[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [stats, setStats] = useState<StatsState>({ todayTotal: 0, websiteToday: 0, adminToday: 0, failuresToday: 0 });
  const [initialLoad, setInitialLoad] = useState(true);
  const [isLive, setIsLive] = useState(true);
  const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchDataRef = useRef<() => void>(() => {});

  const buildFetch = useCallback((currentFilters: FiltersState, currentPage: number) => async () => {
    try {
      const [logsRes, statsRes] = await Promise.all([
        DAV_APIS.audit.getLogs({ ...currentFilters, page: currentPage, limit: 20 }),
        DAV_APIS.audit.getStats(),
      ]);
      setLogs(logsRes.data.data.logs);
      setTotal(logsRes.data.data.total);
      setTotalPages(logsRes.data.data.pages);
      setStats(statsRes.data.data);
      setInitialLoad(false);
    } catch {
      setInitialLoad(false);
    }
  }, []);

  useEffect(() => {
    const fetch = buildFetch(filters, page);
    fetchDataRef.current = fetch;
    fetch();
  }, [filters, page, buildFetch]);

  useEffect(() => {
    if (!isLive) return undefined;
    const id = setInterval(() => fetchDataRef.current(), 5000);
    return () => clearInterval(id);
  }, [isLive]);

  useEffect(() => {
    const handleVisibility = () => setIsLive(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const handleFilterChange = (key: keyof FiltersState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => { setFilters(EMPTY_FILTERS); setPage(1); };
  const hasActiveFilters = Object.values(filters).some((v) => v !== '');

  const toggleRow = (id: string) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <div className='space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700'>
      {/* Header */}
      <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3'>
        <div>
          <h1 className='text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight'>Audit Log</h1>
          <p className='text-gray-500 dark:text-gray-400 mt-1 text-sm'>Complete activity trail — every action across the entire system</p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border ${
          isLive ?
            'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700'
        }`}>
          <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
          {isLive ? 'Live — refreshing every 5s' : 'Paused (tab not focused)'}
        </div>
      </div>

      {/* Stats */}
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-4'>
        <StatCard icon={<HiOutlineChartBar className='w-5 h-5 text-indigo-600 dark:text-indigo-400' />} label='Total Today' value={stats.todayTotal} colorClass='bg-indigo-50 dark:bg-indigo-900/30' />
        <StatCard icon={<HiOutlineGlobeAlt className='w-5 h-5 text-gray-600 dark:text-gray-400' />} label='Website Activity' value={stats.websiteToday} colorClass='bg-gray-100 dark:bg-gray-800' />
        <StatCard icon={<HiOutlineShieldCheck className='w-5 h-5 text-emerald-600 dark:text-emerald-400' />} label='Admin Actions' value={stats.adminToday} colorClass='bg-emerald-50 dark:bg-emerald-900/30' />
        <StatCard icon={<HiOutlineExclamationCircle className='w-5 h-5 text-red-500 dark:text-red-400' />} label='Failures Today' value={stats.failuresToday} colorClass='bg-red-50 dark:bg-red-900/30' />
      </div>

      {/* Filters */}
      <Card variant='glass' className='p-4'>
        <div className='flex flex-wrap items-center gap-3'>
          <div className='relative flex-1 min-w-[200px]'>
            <HiOutlineMagnifyingGlass className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
            <input
              type='text'
              placeholder='Search by email, username, action…'
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className='w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500'
            />
          </div>
          <select value={filters.category} onChange={(e) => handleFilterChange('category', e.target.value)} className='px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500'>
            <option value=''>All Categories</option>
            {Object.entries(CATEGORY_STYLES).map(([cat, s]) => (
              <option key={cat} value={cat}>{s.label}</option>
            ))}
          </select>
          <select value={filters.role} onChange={(e) => handleFilterChange('role', e.target.value)} className='px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500'>
            <option value=''>All Sources</option>
            <option value='website'>Website</option>
            <option value='super_admin'>Super Admin</option>
            <option value='admin'>Admin</option>
            <option value='content_manager'>Content Manager</option>
            <option value='tour_operator'>Tour Operator</option>
            <option value='agent'>Agent</option>
          </select>
          <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} className='px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500'>
            <option value=''>All Statuses</option>
            <option value='success'>Success</option>
            <option value='failure'>Failed</option>
          </select>
          <input type='date' value={filters.dateFrom} onChange={(e) => handleFilterChange('dateFrom', e.target.value)} className='px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500' />
          <span className='text-gray-400 text-sm hidden sm:block'>→</span>
          <input type='date' value={filters.dateTo} onChange={(e) => handleFilterChange('dateTo', e.target.value)} className='px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500' />
          {hasActiveFilters && (
            <button type='button' onClick={clearFilters} className='flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors font-medium'>
              <HiOutlineXMark className='w-4 h-4' />
              Clear
            </button>
          )}
          <p className='ml-auto text-xs text-gray-400 font-medium'>{total.toLocaleString()} result{total !== 1 ? 's' : ''}</p>
        </div>
      </Card>

      {/* Table */}
      <Card variant='glass' className='overflow-hidden border-none shadow-xl shadow-gray-200/50 dark:shadow-none'>
        {initialLoad && logs.length === 0 && (
          <div className='py-16 text-center text-gray-400 text-sm'>Loading audit logs…</div>
        )}
        {!initialLoad && logs.length === 0 && (
          <div className='py-16 text-center text-gray-400 text-sm'>No audit entries found.</div>
        )}
        {logs.length > 0 && (
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30'>
                  <th className='w-8 px-2 py-3' aria-label='Expand' />
                  <th className='text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap'>Timestamp</th>
                  <th className='text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>Actor</th>
                  <th className='text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap'>Category</th>
                  <th className='text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap'>Action</th>
                  <th className='text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>Description</th>
                  <th className='text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider'>Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const cat = CATEGORY_STYLES[log.category];
                  const roleStyle = ROLE_STYLES[log.actor.role] ?? ROLE_STYLES.website;
                  const actorLabel = log.actor.username || log.actor.email || '—';
                  const roleLabel = formatRole(log.actor.role || 'website');
                  const isExpanded = expandedId === log._id;
                  const { date, time } = formatTimestamp(log.createdAt);

                  return (
                    <React.Fragment key={log._id}>
                      <tr
                        onClick={() => toggleRow(log._id)}
                        className={`border-b border-gray-50 dark:border-gray-800/50 cursor-pointer transition-colors ${
                          isExpanded ?
                            'bg-gray-50 dark:bg-gray-800/40'
                            : 'hover:bg-gray-50/60 dark:hover:bg-gray-800/20'
                        }`}
                      >
                        {/* Expand toggle */}
                        <td className='w-8 pl-3 pr-1 py-3 text-gray-400'>
                          <HiOutlineChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`} />
                        </td>

                        {/* Timestamp */}
                        <td className='px-4 py-3 whitespace-nowrap'>
                          <p className='text-xs font-semibold text-gray-700 dark:text-gray-300'>{time}</p>
                          <p className='text-[11px] text-gray-400 dark:text-gray-500 mt-0.5'>{date}</p>
                          <p className='text-[10px] text-gray-400 dark:text-gray-600 italic'>{formatRelative(log.createdAt)}</p>
                        </td>

                        {/* Actor */}
                        <td className='px-4 py-3'>
                          <p className='font-semibold text-gray-900 dark:text-white text-xs truncate max-w-[130px]' title={actorLabel}>{actorLabel}</p>
                          <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize ${roleStyle.bg} ${roleStyle.text}`}>
                            {roleLabel}
                          </span>
                        </td>

                        {/* Category */}
                        <td className='px-4 py-3 whitespace-nowrap'>
                          {cat ? (
                            <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold w-fit ${cat.bg} ${cat.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cat.dot}`} />
                              {cat.label}
                            </span>
                          ) : (
                            <span className='text-gray-400 text-xs'>{log.category}</span>
                          )}
                        </td>

                        {/* Action */}
                        <td className='px-4 py-3 whitespace-nowrap'>
                          <span className='text-xs font-semibold text-gray-700 dark:text-gray-300'>
                            {ACTION_LABELS[log.action] ?? formatRole(log.action)}
                          </span>
                        </td>

                        {/* Description */}
                        <td className='px-4 py-3 max-w-[220px]'>
                          <span className='text-xs text-gray-600 dark:text-gray-400 truncate block' title={log.description}>
                            {log.description}
                          </span>
                        </td>

                        {/* Status */}
                        <td className='px-4 py-3 whitespace-nowrap'>
                          {log.status === 'success' ? (
                            <span className='flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-[11px] font-bold w-fit'>
                              <HiOutlineCheckCircle className='w-3.5 h-3.5' />
                              Success
                            </span>
                          ) : (
                            <span className='flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-[11px] font-bold w-fit'>
                              <HiOutlineXCircle className='w-3.5 h-3.5' />
                              Failed
                            </span>
                          )}
                        </td>
                      </tr>

                      {/* Expanded detail accordion */}
                      {isExpanded && (
                        <tr className='border-b border-gray-100 dark:border-gray-800'>
                          <td colSpan={7} className='p-0'>
                            <ExpandedDetail log={log} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 dark:border-gray-800'>
            <p className='text-xs text-gray-500 dark:text-gray-400'>
              Showing <span className='font-semibold text-gray-700 dark:text-gray-300'>
                {((page - 1) * 20) + 1}–{Math.min(page * 20, total)}
              </span> of <span className='font-semibold text-gray-700 dark:text-gray-300'>{total.toLocaleString()}</span> entries
            </p>
            <div className='flex items-center gap-1'>
              <button
                type='button'
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className='p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
              >
                <HiOutlineChevronLeft className='w-4 h-4' />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                  acc.push(p);
                  return acc;
                }, [])
                .map((item) => {
                  if (item === 'ellipsis') {
                    return <span key={`gap-${Math.random()}`} className='px-1 text-gray-400 text-xs'>…</span>;
                  }
                  const pageNum = item as number;
                  const isActive = page === pageNum;
                  return (
                    <button
                      key={pageNum}
                      type='button'
                      onClick={() => setPage(pageNum)}
                      className={`min-w-[32px] h-8 px-2 rounded-lg text-xs font-semibold transition-colors ${
                        isActive ?
                          'bg-primary-500 text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

              <button
                type='button'
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className='p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
              >
                <HiOutlineChevronRight className='w-4 h-4' />
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AuditPage;
